import type { MissionAssessmentInterSelectDto, TechnologyNestedSelectDto } from '@@types/api.types';
import { MissionAssessmentInterType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { SankeyModal } from '@components/shared/sankey-modal.component';
import { Sankey, type SankeyData, type SankeyProps, type SankeySettings } from '@components/shared/sankey.component';
import {
  type SankeyChartConfig,
  SankeyDataBuilder,
  calculateSankeySize,
  createSankeyChartUtils,
} from '@utils/sankey.util';
import { ZoomInIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Node types for the Technology Platform Alignment
 */
type NodeType = 'interchangeable' | 'technology' | 'interoperable';

/**
 * Context data for technology platform alignment nodes
 */
type NodeContext = {
  type: NodeType;
};

/**
 * Props for the Technology Platform Alignment Sankey component
 */
type TechnologyPlatformAlignmentSankeyProps = {
  inters: MissionAssessmentInterSelectDto[];
  sankeyProps?: Partial<SankeyProps>;
  settings?: Partial<SankeySettings>;
  technology: TechnologyNestedSelectDto;
};

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Configuration for Technology Platform Alignment Sankey chart
 * Defines colors and labels for each node type
 */
const TECHNOLOGY_PLATFORM_ALIGNMENT_CONFIG: SankeyChartConfig<NodeType> = {
  nodeTypes: {
    interchangeable: {
      color: '#10B981', // Emerald
      label: 'Interchangeable',
    },
    technology: {
      color: '#3B82F6', // Blue
      label: 'Technology',
    },
    interoperable: {
      color: '#8B5CF6', // Violet
      label: 'Interoperable',
    },
  },
};

// Create chart utilities using the configuration
const chartUtils = createSankeyChartUtils(TECHNOLOGY_PLATFORM_ALIGNMENT_CONFIG);

// =============================================================================
// DATA PROCESSING
// =============================================================================

/**
 * Processes technology platform alignment data to create Sankey-compatible data structure
 *
 * Creates a flow from Interchangeable -> Technology -> Interoperable
 *
 * @param technology - Technology data object
 * @param inters - Array of interoperability data
 * @returns Processed Sankey data structure
 */
function processTechnologyPlatformAlignmentData(
  technology: TechnologyNestedSelectDto,
  inters: MissionAssessmentInterSelectDto[],
): SankeyData<Record<string, unknown>, NodeContext> {
  const builder = new SankeyDataBuilder<NodeContext>();

  if (!inters.length) {
    return builder.getData();
  }

  // Filter interchangeable and interoperable items
  const interchangeableItems = inters.filter((inter) => inter.type === MissionAssessmentInterType.INTERCHANGEABLE);
  const interoperableItems = inters.filter((inter) => inter.type === MissionAssessmentInterType.INTEROPERABLE);

  // Only proceed if we have data for BOTH sides
  if (!interchangeableItems.length || !interoperableItems.length) {
    return builder.getData();
  }

  // Add technology node (center)
  builder.addNode({
    name: technology.name,
    type: 'technology',
    extraData: { type: 'technology' },
  });

  // Calculate flow values so that the *sum* of flows from the Interchangeable column
  // equals the *sum* of flows leaving the Technology node towards the Interoperable column.
  // This ensures both sides occupy the same overall height.
  const totalInteroperableItems = interoperableItems.length;
  const totalInterchangeableItems = interchangeableItems.length;

  // Each interchangeable node gets the same flow value so that:
  //    flowValue * totalInterchangeableItems === totalInteroperableItems
  // If the division is not even, we allow fractional flow values – d3-sankey supports this.
  const interchangeableFlowValue = totalInteroperableItems / totalInterchangeableItems;

  // Guard against division by zero (shouldn't happen because we early-return when either side is empty)
  if (!Number.isFinite(interchangeableFlowValue) || interchangeableFlowValue <= 0) {
    return builder.getData();
  }

  // Add interchangeable nodes (left column)
  interchangeableItems.forEach((item, index) => {
    const nodeName = item.name || `${item.category.name}${item.subcategory ? ` - ${item.subcategory.name}` : ''}`;

    builder.addNode({
      name: nodeName,
      type: 'interchangeable',
      extraData: { type: 'interchangeable' },
    });

    // Add link from interchangeable to technology with higher flow value
    builder.addLink({
      sourceName: nodeName,
      sourceType: 'interchangeable',
      targetName: technology.name,
      targetType: 'technology',
      value: interchangeableFlowValue,
    });
  });

  // Add interoperable nodes (right column)
  interoperableItems.forEach((item, index) => {
    const nodeName = item.name || `${item.category.name}${item.subcategory ? ` - ${item.subcategory.name}` : ''}`;

    builder.addNode({
      name: nodeName,
      type: 'interoperable',
      extraData: { type: 'interoperable' },
    });

    // Add link from technology to interoperable with standard flow value
    builder.addLink({
      sourceName: technology.name,
      sourceType: 'technology',
      targetName: nodeName,
      targetType: 'interoperable',
      value: 1,
    });
  });

  return builder.getData();
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Technology Platform Alignment Sankey Chart
 *
 * Visualizes the platform alignment from interchangeable technologies through the main technology to interoperable technologies.
 *
 * Flow: Interchangeable -> Technology -> Interoperable
 */
const TechnologyPlatformAlignmentSankeyChart: React.FC<TechnologyPlatformAlignmentSankeyProps> = (props) => {
  const { technology, inters, sankeyProps, settings } = props;

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  function handleOpenFullScreen() {
    setIsOpenFullScreenModal(true);
  }

  function handleCloseFullScreen() {
    setIsOpenFullScreenModal(false);
  }

  // Extract signature calculation into separate memoized value
  const dataSignature = useMemo(() => {
    return `${technology.id}-${inters.length}-${inters.map((i) => `${i.type}-${i.name || i.category.name}`).join(',')}`;
  }, [technology.id, inters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we already track data changes with the dataSignature
  const sankeyData = useMemo(() => {
    return processTechnologyPlatformAlignmentData(technology, inters);
  }, [dataSignature]);

  // Automatically determine size preset based on data volume
  const sizePreset = calculateSankeySize(sankeyData);

  if (!sankeyData.nodes.length || !sankeyData.links.length) {
    return null;
  }

  return (
    <section
      className="flex flex-col"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="ml-auto">
        <IconButton
          icon={<ZoomInIcon />}
          isVisible={isHovering}
          onClick={handleOpenFullScreen}
          variant="secondary"
        />
      </div>

      <div className="w-full">
        <Sankey
          data={sankeyData}
          formatNodeLabel={chartUtils.formatNodeLabel}
          formatNodeSubtext={chartUtils.formatNodeSubtext}
          size={sizePreset}
          {...sankeyProps}
          onOpenFullScreen={handleOpenFullScreen}
          settings={{
            ...settings,
            colorMap: { ...chartUtils.getColorMap(), ...settings?.colorMap },
            height: settings?.height ?? 'auto',
          }}
        />
      </div>

      {isOpenFullScreenModal && (
        <SankeyModal
          isOpen={isOpenFullScreenModal}
          onClose={handleCloseFullScreen}
          title="Platform Alignment"
        >
          {({ settings: modalSettings }) => (
            <Sankey
              data={sankeyData}
              formatNodeLabel={chartUtils.formatNodeLabel}
              formatNodeSubtext={chartUtils.formatNodeSubtext}
              size={sizePreset}
              {...sankeyProps}
              settings={{
                ...modalSettings,
                colorMap: { ...chartUtils.getColorMap(), ...modalSettings?.colorMap },
              }}
            />
          )}
        </SankeyModal>
      )}
    </section>
  );
};

export const TechnologyPlatformAlignmentSankey = React.memo(TechnologyPlatformAlignmentSankeyChart);
